// client/src/pages/School_Admin/Academics/TimetableMasterGrid.js
//
// THE WALL CHART.
//
// One table, every class, every period, one block per day — the thing a school
// actually works from. The per-teacher and per-class views below are DERIVED
// from it and never edited separately, so there is one week and six ways of
// looking at it rather than six weeks that must agree.
//
// COLUMNS ARE CLOCK TIMES, NOT PERIOD NUMBERS. Classes close at different times
// and run different period lengths, so Form 1A's third period and Upper Sixth's
// third are not the same minutes and cannot share a column header. The columns
// are the union of every distinct start time that day; a class with no period at
// one of them shows as not in session, which is the truth.
//
// BREAKS ARE DRAWN. They are not lessons and the solver never places into them,
// but a chart that runs period 3 straight into period 4 is not the day anybody
// recognises — the columns stop matching the clock on the wall, and a teacher
// reading it cannot tell whether they have a free period or a break.
//
// ONE CELL PER PERIOD, ALWAYS. A double used to draw as a single chip spanning
// two columns, which reads well and makes every row a different shape: the
// columns stop lining up down the page, and counting periods by eye stops
// working. Both halves of a double now fill their own cell and say so.
//
// A CHIP CARRIES ITS OWN MEANING. The subject code with the teacher's number
// (ICT3), the teacher's name beneath it in small type, and a family colour —
// in that order of importance. These charts are photocopied in black and white
// and pinned to a wall, so the colour is the last thing that survives and the
// text has to be enough on its own.

import React, { useState, useMemo } from 'react';
import {
  Alert, Box, Button, MenuItem, Paper, Select, Stack, Tab, Tabs,
  Typography, useTheme, ToggleButton, ToggleButtonGroup, Tooltip,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { subjectChipColours, legendFor } from '../../../utils/subjectColours.generated';

const DAY_NAME = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const toMin = (v) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(v || ''));
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

/* -------------------------------------------------------------------------- */

/**
 * The ground a header cell sits on.
 *
 * NOT `palette.grey[N]`. This product's grey scale is a LADDER built from the
 * chosen palette rather than a fixed set of greys — `grey[900]` is that
 * palette's TEXT colour. In the dark theme that is #E8ECF2, very nearly white,
 * so a header painted with it got near-white ink on a near-white ground and the
 * period numbers and their times simply disappeared.
 *
 * `action.selected` and `action.hover` are overlays MUI derives from the mode
 * itself: a light wash on a dark ground, a dark wash on a light one. They cannot
 * invert, whichever palette a school picks.
 */
const headerGround = (theme) => theme.palette.action.selected;

/**
 * The columns of one day: every distinct start time across the classes given,
 * each marked as a period or a break.
 *
 * A start time counts as a break only when EVERY class in session then treats it
 * as one. Schools normally share their break times, but the day config can be
 * overridden per class, and a column labelled "Break" while one class is sitting
 * a lesson in it would be a chart that lies.
 */
function columnsOf(classes, day) {
  const seen = new Map();     // start -> {start, end, periodFor:Set, breakFor:Set}
  for (const c of classes) {
    if (day != null && !(c.days || []).includes(day)) continue;
    const slots = (c.daySlots && c.daySlots.length)
      ? c.daySlots
      : (c.periods || []).map((p) => ({ ...p, type: 'period' }));
    for (const s of slots) {
      let held = seen.get(s.start);
      if (!held) { held = { start: s.start, end: s.end, period: 0, brk: 0, label: null }; seen.set(s.start, held); }
      if (s.type === 'break') { held.brk += 1; held.label = held.label || s.label || 'Break'; }
      else held.period += 1;
    }
  }

  const cols = [...seen.values()].sort((a, b) => toMin(a.start) - toMin(b.start));
  let n = 0;
  return cols.map((c) => {
    const isBreak = c.brk > 0 && c.period === 0;
    if (!isBreak) n += 1;
    return { ...c, type: isBreak ? 'break' : 'period', number: isBreak ? null : n };
  });
}

/** What one class is doing at one start time: a period, a break, or nothing. */
function slotKind(cls, start) {
  const slots = (cls.daySlots && cls.daySlots.length)
    ? cls.daySlots
    : (cls.periods || []).map((p) => ({ ...p, type: 'period' }));
  const held = slots.find((s) => s.start === start);
  return held ? (held.type === 'break' ? 'break' : 'period') : 'closed';
}

/* -------------------------------------------------------------------------- */

/** One period of one subject, as it appears in a cell. */
function PeriodChip({ placement, dark, dense, part }) {
  const colours = subjectChipColours({
    name: placement.subjectName,
    code: placement.subjectCode,
    colorHex: placement.colorHex,
    dark,
  });
  return (
    <Box
      sx={{
        borderLeft: `3px solid ${colours.rule}`,
        bgcolor: colours.background,
        color: colours.ink,
        borderRadius: '3px',
        px: 0.75,
        py: dense ? 0.25 : 0.5,
        minWidth: 0,
        lineHeight: 1.15,
      }}
    >
      <Stack direction="row" spacing={0.5} alignItems="baseline">
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, fontSize: dense ? 10 : 11.5, letterSpacing: 0.2 }}
        >
          {placement.label || placement.subjectCode || placement.subjectName}
        </Typography>
        {/* WHICH HALF OF A DOUBLE THIS IS. Every period fills its own cell, so
            without this the two halves of a double are indistinguishable from
            the same subject scheduled twice in a row by accident. */}
        {part && (
          <Typography variant="caption" sx={{ fontSize: dense ? 8 : 9, opacity: 0.7 }}>
            {part}
          </Typography>
        )}
      </Stack>
      {/* THE TEACHER'S NAME UNDERNEATH IN SMALL TYPE. Without it the chart says
          which subject and not who, and "who" is the question a staffroom asks. */}
      <Typography
        variant="caption"
        sx={{ display: 'block', fontSize: dense ? 8.5 : 9.5, opacity: 0.85, whiteSpace: 'nowrap',
          overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        {placement.teacherName || '—'}
      </Typography>
    </Box>
  );
}

/** Which period of a run this slot is, as "1/2", or null for a single. */
function partLabel(placement, start) {
  if (!placement.slots || placement.slots.length < 2) return null;
  const i = placement.slots.findIndex((s) => s.start === start);
  return i < 0 ? null : `${i + 1}/${placement.slots.length}`;
}

/* -------------------------------------------------------------------------- */

/** The grid for one day: rows are classes, columns are the clock. */
function DayGrid({ day, classes, placements, dark, dense }) {
  const theme = useTheme();

  const inSession = useMemo(
    () => classes.filter((c) => (c.days || []).includes(day)),
    [classes, day],
  );
  const columns = useMemo(() => columnsOf(classes, day), [classes, day]);

  const byClass = useMemo(() => {
    const map = new Map();
    for (const p of placements) {
      if (p.day !== day) continue;
      if (!map.has(p.classId)) map.set(p.classId, []);
      map.get(p.classId).push(p);
    }
    return map;
  }, [placements, day]);

  if (!columns.length || !inSession.length) return null;

  const line = `1px solid ${theme.palette.divider}`;
  const heavy = `2px solid ${theme.palette.divider}`;
  const breakBg = dark ? 'rgba(255,193,7,0.12)' : 'rgba(255,193,7,0.16)';

  return (
    <Box sx={{ mb: 3.5 }}>
      {/* THE DAY, AS A BAND. A one-line caption above a table was lost the
          moment two days were on screen together. */}
      <Box sx={{
        bgcolor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        px: 1.25, py: 0.5, borderRadius: '4px 4px 0 0',
      }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0.4 }}>
          {DAY_NAME[day].toUpperCase()}
        </Typography>
      </Box>

      <Box sx={{ overflowX: 'auto', border: line, borderTop: 0, borderRadius: '0 0 4px 4px' }}>
        <Box
          component="table"
          sx={{
            borderCollapse: 'separate', borderSpacing: 0, width: '100%',
            minWidth: 150 + columns.length * 98,
            '& td, & th': { border: 0, borderBottom: line, borderRight: line, p: 0.25, verticalAlign: 'top' },
          }}
        >
          <thead>
            <tr>
              <Box component="th" sx={{
                position: 'sticky', left: 0, zIndex: 3, minWidth: 150, textAlign: 'left',
                bgcolor: headerGround(theme),
                borderRight: `${heavy} !important`, px: 1,
              }}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>CLASS</Typography>
              </Box>
              {columns.map((col) => (
                <Box
                  component="th"
                  key={col.start}
                  sx={{
                    minWidth: col.type === 'break' ? 58 : 98,
                    width: col.type === 'break' ? 58 : undefined,
                    bgcolor: col.type === 'break' ? breakBg : headerGround(theme),
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', fontSize: 11 }}>
                    {col.type === 'break' ? (col.label || 'Break') : `Period ${col.number}`}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: 9.5, opacity: 0.75 }}>
                    {col.start}–{col.end}
                  </Typography>
                </Box>
              ))}
            </tr>
          </thead>
          <tbody>
            {inSession.map((cls, rowIndex) => {
              const mine = byClass.get(cls.id) || [];
              return (
                <tr key={cls.id}>
                  <Box component="td" sx={{
                    position: 'sticky', left: 0, zIndex: 2, minWidth: 150, px: 1,
                    borderRight: `${heavy} !important`,
                    // Banded rows, because reading across sixteen columns to the
                    // wrong class is the single easiest mistake to make here.
                    bgcolor: rowIndex % 2
                      ? theme.palette.action.hover
                      : theme.palette.background.paper,
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>{cls.name}</Typography>
                  </Box>

                  {columns.map((col) => {
                    const kind = slotKind(cls, col.start);
                    const here = mine.filter((p) => (p.slots || []).some((s) => s.start === col.start));

                    return (
                      <Box
                        component="td"
                        key={col.start}
                        sx={{
                          bgcolor: kind === 'break'
                            ? breakBg
                            : (kind === 'closed'
                              ? theme.palette.action.disabledBackground
                              : (rowIndex % 2 ? theme.palette.action.hover : undefined)),
                          minWidth: col.type === 'break' ? 58 : 98,
                          textAlign: kind === 'break' ? 'center' : undefined,
                        }}
                      >
                        {kind === 'break' ? (
                          <Typography variant="caption" sx={{ fontSize: 9, opacity: 0.8 }}>
                            {col.label || 'Break'}
                          </Typography>
                        ) : (
                          /* A CLASH SET IS ONE PERIOD HOLDING SEVERAL SUBJECTS.
                             Stacked, because that is what the class actually
                             does: everyone is in one of them at that moment. */
                          <Stack spacing={0.25}>
                            {here
                              .sort((a, b) => String(a.subjectName).localeCompare(String(b.subjectName)))
                              .map((p) => (
                                <PeriodChip
                                  key={`${p.unitId}|${col.start}`}
                                  placement={p}
                                  dark={dark}
                                  dense={dense}
                                  part={partLabel(p, col.start)}
                                />
                              ))}
                          </Stack>
                        )}
                      </Box>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Box>
      </Box>
    </Box>
  );
}

/* -------------------------------------------------------------------------- */

/** A single week for one teacher or one class: rows are days, columns the clock. */
function OneWeek({ title, subtitle, days, columns, cellFor, empty }) {
  const theme = useTheme();
  const line = `1px solid ${theme.palette.divider}`;
  const heavy = `2px solid ${theme.palette.divider}`;
  const dark = theme.palette.mode === 'dark';
  const breakBg = dark ? 'rgba(255,193,7,0.12)' : 'rgba(255,193,7,0.16)';

  if (!columns.length || !days.length) return <Alert severity="info">{empty}</Alert>;

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
      {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
      <Box sx={{ overflowX: 'auto', mt: 1, border: line, borderRadius: 1 }}>
        <Box component="table" sx={{
          borderCollapse: 'separate', borderSpacing: 0, width: '100%',
          minWidth: 130 + columns.length * 104,
          '& td, & th': { border: 0, borderBottom: line, borderRight: line, p: 0.25, verticalAlign: 'top' },
        }}>
          <thead>
            <tr>
              <Box component="th" sx={{
                minWidth: 130, textAlign: 'left', px: 1,
                borderRight: `${heavy} !important`,
                bgcolor: headerGround(theme),
              }}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>DAY</Typography>
              </Box>
              {columns.map((col) => (
                <Box component="th" key={col.start} sx={{
                  minWidth: col.type === 'break' ? 62 : 104,
                  bgcolor: col.type === 'break' ? breakBg : headerGround(theme),
                }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', fontSize: 11 }}>
                    {col.type === 'break' ? (col.label || 'Break') : `Period ${col.number}`}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: 9.5, opacity: 0.75 }}>
                    {col.start}–{col.end}
                  </Typography>
                </Box>
              ))}
            </tr>
          </thead>
          <tbody>
            {days.map((day, i) => (
              <tr key={day}>
                <Box component="td" sx={{
                  px: 1, borderRight: `${heavy} !important`,
                  bgcolor: i % 2 ? theme.palette.action.hover : theme.palette.background.paper,
                }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>{DAY_NAME[day]}</Typography>
                </Box>
                {columns.map((col) => (
                  <Box component="td" key={col.start} sx={{
                    minWidth: col.type === 'break' ? 62 : 104,
                    textAlign: col.type === 'break' ? 'center' : undefined,
                    bgcolor: col.type === 'break'
                      ? breakBg
                      : (i % 2 ? theme.palette.action.hover : undefined),
                  }}>
                    {col.type === 'break'
                      ? <Typography variant="caption" sx={{ fontSize: 9, opacity: 0.8 }}>{col.label || 'Break'}</Typography>
                      : cellFor(day, col.start)}
                  </Box>
                ))}
              </tr>
            ))}
          </tbody>
        </Box>
      </Box>
    </Box>
  );
}

/* -------------------------------------------------------------------------- */

/** Rows of the master grid as CSV, for a spreadsheet or a printer that is not here. */
function toCsv(classes, placements) {
  const q = (s) => `"${String(s ?? '').replace(/"/g, '""')}"`;
  const lines = [['Day', 'Class', 'Start', 'End', 'Subject', 'Code', 'Teacher'].map(q).join(',')];
  const byId = new Map(classes.map((c) => [c.id, c.name]));
  const sorted = [...placements].sort((a, b) => a.day - b.day
    || String(a.slots[0].start).localeCompare(String(b.slots[0].start))
    || String(byId.get(a.classId)).localeCompare(String(byId.get(b.classId))));
  for (const p of sorted) {
    for (const s of p.slots) {
      lines.push([DAY_NAME[p.day], byId.get(p.classId) || p.classId, s.start, s.end,
        p.subjectName, p.label || p.subjectCode || '', p.teacherName].map(q).join(','));
    }
  }
  return lines.join('\n');
}

/* -------------------------------------------------------------------------- */

/**
 * WHO IS TEACHING A PLACEMENT — `u:93`, `p:4`, or null for a period with nobody
 * against it yet.
 *
 * The same scheme the server keys availability, caps and allocation on, so a
 * placeholder's sheet is built from their own periods rather than from whatever
 * `Number(null)` happens to collide with.
 */
const identityOf = (p) => (p?.teacherUserId != null
  ? `u:${Number(p.teacherUserId)}`
  : (p?.placeholderTeacherId != null ? `p:${Number(p.placeholderTeacherId)}` : null));

/* -------------------------------------------------------------------------- */

/**
 * ONE SHEET, ONE PDF — landscape, and nothing but the timetable.
 *
 * `window.print()` printed the whole application: the navigation, the tab strip,
 * the readiness report, the run history, and somewhere inside all of it the
 * chart somebody actually wanted, in portrait, cut off at the sixth column.
 * A PDF built from the data has none of those problems, comes out in landscape
 * by construction, has selectable text, and is the same file whether it is saved
 * or sent to a printer.
 */
function buildPdf({ title, subtitle, tables }) {
  const doc = new jsPDF('l', 'mm', 'a4');
  const width = doc.internal.pageSize.getWidth();

  tables.forEach((table, i) => {
    if (i > 0) doc.addPage('a4', 'l');

    doc.setFontSize(15);
    doc.text(title, 14, 15);
    if (subtitle) {
      doc.setFontSize(9.5);
      doc.setTextColor(110);
      doc.text(subtitle, 14, 21);
      doc.setTextColor(0);
    }
    if (table.caption) {
      doc.setFontSize(11);
      doc.text(table.caption, 14, subtitle ? 28 : 23);
    }

    autoTable(doc, {
      head: [table.head],
      body: table.body,
      startY: (subtitle ? 28 : 23) + (table.caption ? 4 : 0),
      theme: 'grid',
      styles: { fontSize: 7.2, cellPadding: 1.4, overflow: 'linebreak', valign: 'middle', lineWidth: 0.1 },
      headStyles: { fillColor: [55, 65, 81], textColor: 255, fontStyle: 'bold', fontSize: 7.4 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 30, fillColor: [243, 244, 246] } },
      // BREAKS IN THE SAME AMBER THEY WEAR ON SCREEN, so the paper and the page
      // are recognisably the same chart.
      //
      // Down a column here and across a row on the designer's sheet, which lays
      // the same week out the other way round — periods down the side, days
      // across the top. A break is a break in either orientation.
      didParseCell: (data) => {
        if (table.breakColumns?.has(data.column.index)
          || (data.section === 'body' && table.breakRows?.has(data.row.index))) {
          data.cell.styles.fillColor = [255, 248, 225];
          data.cell.styles.textColor = [146, 64, 14];
          data.cell.styles.halign = 'center';
          data.cell.styles.fontSize = 6.4;
        }
      },
      margin: { left: 10, right: 10 },
    });

    doc.setFontSize(7.5);
    doc.setTextColor(140);
    doc.text(
      `${new Date().toLocaleDateString()}  ·  page ${i + 1} of ${tables.length}`,
      width - 12, doc.internal.pageSize.getHeight() - 8, { align: 'right' },
    );
    doc.setTextColor(0);
  });

  return doc;
}

/** Save it, or hand it to the printer already open at the print dialog. */
function deliver(doc, filename, printIt) {
  if (!printIt) { doc.save(filename); return; }

  // A hidden iframe rather than window.open: a popup blocker silently swallows
  // the second, and a print button that does nothing is worse than no button.
  doc.autoPrint();
  const url = doc.output('bloburl');
  const frame = document.createElement('iframe');
  frame.style.position = 'fixed';
  frame.style.right = '0';
  frame.style.bottom = '0';
  frame.style.width = '0';
  frame.style.height = '0';
  frame.style.border = '0';
  frame.src = url;
  document.body.appendChild(frame);
  // Left in place long enough for the dialog to take ownership of the document;
  // removing it immediately cancels the print in Chrome.
  window.setTimeout(() => { try { document.body.removeChild(frame); } catch (_) { /* gone already */ } }, 60000);
}

/* -------------------------------------------------------------------------- */

export default function MasterGrid({ result, teachers = [] }) {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';

  const [view, setView] = useState('master');
  const [dense, setDense] = useState(false);
  const [teacherId, setTeacherId] = useState('');
  const [classId, setClassId] = useState('');

  // Wrapped, because a fresh [] on every render makes every memo below
  // recompute the whole grid on every render.
  const classes = useMemo(() => result?.classes || [], [result]);
  const placements = useMemo(() => result?.placements || [], [result]);

  const days = useMemo(() => {
    const all = new Set();
    for (const c of classes) for (const d of c.days) all.add(d);
    return [...all].sort((a, b) => a - b);
  }, [classes]);

  const teacherIds = useMemo(() => {
    const seen = new Map();
    for (const p of placements) {
      const key = identityOf(p);
      // A PERIOD WITH NOBODY AGAINST IT IS NOT A TEACHER, and a placeholder is
      // not user zero. Keyed on a bare user id both became the same entry —
      // `Number(null)` is 0 — so the picker offered one "Teacher 0" holding
      // every unstaffed period in the school and every placeholder's periods
      // piled in with them.
      if (!key) continue;
      seen.set(key, p.teacherName || key);
    }
    return [...seen.entries()].sort((a, b) => String(a[1]).localeCompare(String(b[1])));
  }, [placements]);

  const legend = useMemo(
    () => legendFor(placements.map((p) => ({ name: p.subjectName, code: p.subjectCode })), dark),
    [placements, dark],
  );

  const currentTeacher = teacherId || teacherIds[0]?.[0];
  const currentClass = Number(classId) || classes[0]?.id;

  const downloadCsv = () => {
    const blob = new Blob([toCsv(classes, placements)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `timetable-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!placements.length) {
    return <Alert severity="info">Nothing has been generated yet.</Alert>;
  }

  /* -- the derived views ---------------------------------------------------- */

  const teacherName = teacherIds.find(([id]) => id === currentTeacher)?.[1] || '';
  const teacherPeriods = placements.filter((p) => identityOf(p) === currentTeacher);
  const teacherDays = [...new Set(teacherPeriods.map((p) => p.day))].sort((a, b) => a - b);
  // A teacher moves between classes, so their sheet uses the school's day rather
  // than any one class's — otherwise their breaks disappear the moment they
  // teach two classes whose grids differ.
  const teacherColumns = columnsOf(classes, null).filter((col) => col.type === 'break'
    || teacherPeriods.some((p) => (p.slots || []).some((s) => s.start === col.start)));

  const cls = classes.find((c) => c.id === currentClass);
  const classPeriods = placements.filter((p) => p.classId === currentClass);
  const classColumns = cls ? columnsOf([cls], null) : [];

  const teacherWeek = () => {
    const info = teachers.find((t) => t.key === currentTeacher);
    const count = teacherPeriods.reduce((n, p) => n + p.slots.length, 0);
    return (
      <OneWeek
        title={teacherName}
        subtitle={`${count} periods across ${teacherDays.length} day${teacherDays.length === 1 ? '' : 's'}`
          + (info ? ` · can hold ${info.capacity}` : '')}
        days={teacherDays}
        columns={teacherColumns}
        empty="This teacher has no periods in the generated week."
        cellFor={(day, start) => {
          const here = teacherPeriods.filter((p) => p.day === day && p.slots.some((s) => s.start === start));
          if (!here.length) return null;
          return (
            <Stack spacing={0.25}>
              {here.map((p) => (
                <Box key={`${p.unitId}|${start}`}>
                  <PeriodChip placement={p} dark={dark} dense={dense} part={partLabel(p, start)} />
                  {/* WHICH CLASS, on a teacher's own sheet — the one thing the
                      master grid carries in its row header and this does not. */}
                  <Typography variant="caption" sx={{ fontSize: 9, opacity: 0.8, pl: 0.75 }}>
                    {classes.find((c) => c.id === p.classId)?.name}
                  </Typography>
                </Box>
              ))}
            </Stack>
          );
        }}
      />
    );
  };

  const classWeek = () => {
    const count = classPeriods.reduce((n, p) => n + p.slots.length, 0);
    const free = (cls?.periods.length || 0) * (cls?.days.length || 0) - count;
    return (
      <OneWeek
        title={cls?.name || ''}
        subtitle={`${count} periods taught · ${free} free`}
        days={cls?.days || []}
        columns={classColumns}
        empty="This class has no periods in the generated week."
        cellFor={(day, start) => {
          const here = classPeriods.filter((p) => p.day === day && p.slots.some((s) => s.start === start));
          if (!here.length) return null;
          return (
            <Stack spacing={0.25}>
              {here.map((p) => (
                <PeriodChip key={`${p.unitId}|${start}`} placement={p} dark={dark} dense={dense}
                  part={partLabel(p, start)} />
              ))}
            </Stack>
          );
        }}
      />
    );
  };

  /* -- paper ---------------------------------------------------------------- */

  const cellText = (list, start) => list
    .filter((p) => (p.slots || []).some((s) => s.start === start))
    .map((p) => {
      const part = partLabel(p, start);
      return `${p.label || p.subjectCode || p.subjectName}${part ? ` (${part})` : ''}`
        + `\n${p.teacherName || '—'}`;
    })
    .join('\n');

  /** The current view as pages an A4 landscape sheet can hold. */
  const sheet = () => {
    if (view === 'teachers') {
      const brk = new Set();
      teacherColumns.forEach((c, i) => { if (c.type === 'break') brk.add(i + 1); });
      return {
        title: teacherName || 'Teacher',
        subtitle: `${teacherPeriods.reduce((n, p) => n + p.slots.length, 0)} periods`
          + ` across ${teacherDays.length} day${teacherDays.length === 1 ? '' : 's'}`,
        filename: `timetable-${(teacherName || 'teacher').replace(/[^\w-]+/g, '-').toLowerCase()}.pdf`,
        tables: [{
          head: ['Day', ...teacherColumns.map((c) => (c.type === 'break'
            ? `${c.label || 'Break'}\n${c.start}`
            : `Period ${c.number}\n${c.start}–${c.end}`))],
          body: teacherDays.map((day) => [
            DAY_NAME[day],
            ...teacherColumns.map((c) => (c.type === 'break'
              ? (c.label || 'Break')
              : (() => {
                const here = teacherPeriods.filter((p) => p.day === day
                  && p.slots.some((s) => s.start === c.start));
                return here.map((p) => {
                  const part = partLabel(p, c.start);
                  const room = classes.find((x) => x.id === p.classId)?.name || '';
                  return `${p.label || p.subjectCode || p.subjectName}${part ? ` (${part})` : ''}\n${room}`;
                }).join('\n');
              })())),
          ]),
          breakColumns: brk,
        }],
      };
    }

    if (view === 'classes') {
      const brk = new Set();
      classColumns.forEach((c, i) => { if (c.type === 'break') brk.add(i + 1); });
      return {
        title: cls?.name || 'Class',
        subtitle: `${classPeriods.reduce((n, p) => n + p.slots.length, 0)} periods a week`,
        filename: `timetable-${(cls?.name || 'class').replace(/[^\w-]+/g, '-').toLowerCase()}.pdf`,
        tables: [{
          head: ['Day', ...classColumns.map((c) => (c.type === 'break'
            ? `${c.label || 'Break'}\n${c.start}`
            : `Period ${c.number}\n${c.start}–${c.end}`))],
          body: (cls?.days || []).map((day) => [
            DAY_NAME[day],
            ...classColumns.map((c) => (c.type === 'break'
              ? (c.label || 'Break')
              : cellText(classPeriods.filter((p) => p.day === day), c.start))),
          ]),
          breakColumns: brk,
        }],
      };
    }

    // ONE PAGE PER DAY. Every class across sixteen columns does not fit beside
    // another day on one sheet, and a chart squeezed to fit is a chart nobody
    // reads off a wall.
    return {
      title: 'Master timetable',
      subtitle: `${classes.length} classes · ${placements.reduce((n, p) => n + p.slots.length, 0)} periods`,
      filename: `timetable-master-${new Date().toISOString().slice(0, 10)}.pdf`,
      tables: days.map((day) => {
        const cols = columnsOf(classes, day);
        const brk = new Set();
        cols.forEach((c, i) => { if (c.type === 'break') brk.add(i + 1); });
        return {
          caption: DAY_NAME[day],
          head: ['Class', ...cols.map((c) => (c.type === 'break'
            ? `${c.label || 'Break'}\n${c.start}`
            : `Period ${c.number}\n${c.start}–${c.end}`))],
          body: classes.filter((c) => c.days.includes(day)).map((row) => {
            const mine = placements.filter((p) => p.classId === row.id && p.day === day);
            return [
              row.name,
              ...cols.map((c) => {
                const kind = slotKind(row, c.start);
                if (kind === 'break') return c.label || 'Break';
                if (kind === 'closed') return '—';
                return cellText(mine, c.start);
              }),
            ];
          }),
          breakColumns: brk,
        };
      }),
    };
  };

  const toPaper = (printIt) => {
    const s = sheet();
    deliver(buildPdf(s), s.filename, printIt);
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <Tabs value={view} onChange={(_, v) => setView(v)} sx={{ minHeight: 34, '& .MuiTab-root': { minHeight: 34, textTransform: 'none' } }}>
          <Tab value="master" label="Master" />
          <Tab value="teachers" label="Teachers" />
          <Tab value="classes" label="Classes" />
        </Tabs>
        <Box sx={{ flex: 1 }} />
        {view === 'teachers' && (
          <Select size="small" value={currentTeacher || ''} onChange={(e) => setTeacherId(e.target.value)} sx={{ minWidth: 190 }}>
            {teacherIds.map(([id, name]) => <MenuItem key={id} value={id}>{name}</MenuItem>)}
          </Select>
        )}
        {view === 'classes' && (
          <Select size="small" value={currentClass || ''} onChange={(e) => setClassId(e.target.value)} sx={{ minWidth: 190 }}>
            {classes.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </Select>
        )}
        <ToggleButtonGroup size="small" exclusive value={dense} onChange={(_, v) => v !== null && setDense(v)}>
          <ToggleButton value={false}>Roomy</ToggleButton>
          <ToggleButton value>Dense</ToggleButton>
        </ToggleButtonGroup>
        <Tooltip title="This timetable only, landscape, straight to the print dialog">
          <Button size="small" startIcon={<PrintIcon />} onClick={() => toPaper(true)}>Print</Button>
        </Tooltip>
        <Tooltip title="This timetable as a landscape PDF">
          <Button size="small" startIcon={<PictureAsPdfIcon />} onClick={() => toPaper(false)}>PDF</Button>
        </Tooltip>
        <Tooltip title="The whole week as a spreadsheet">
          <Button size="small" startIcon={<DownloadIcon />} onClick={downloadCsv}>CSV</Button>
        </Tooltip>
      </Stack>

      {/* COLOUR IS NEVER THE ONLY CARRIER OF MEANING, so the legend names the
          families rather than standing in for the chips' own labels. */}
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        {legend.map((f) => (
          <Stack key={f.id} direction="row" spacing={0.5} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: f.rule }} />
            <Typography variant="caption" color="text.secondary">{f.label}</Typography>
          </Stack>
        ))}
        <Box sx={{ flex: 1 }} />
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: 'rgba(255,193,7,0.5)' }} />
          <Typography variant="caption" color="text.secondary">Break</Typography>
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ p: 1.5 }}>
        {view === 'master' && days.map((day) => (
          <DayGrid key={day} day={day} classes={classes} placements={placements} dark={dark} dense={dense} />
        ))}
        {view === 'teachers' && teacherWeek()}
        {view === 'classes' && classWeek()}
      </Paper>
    </Box>
  );
}

export { toCsv, columnsOf, partLabel, buildPdf, deliver };
